import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SectionHeader from '../../../components/shared/SectionHeader';
import CurrencyField from '../../../components/form/CurrencyField';

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

const LiabilitiesStep = () => {
  const { setValue } = useFormContext();
  const [creditCards, carLoans, studentLoans, otherDebts, alimony, totalIncome] = useWatch({
    name: [
      'liabilities.creditCardDebt',
      'liabilities.carLoans',
      'liabilities.studentLoans',
      'liabilities.otherDebts',
      'liabilities.alimonyChildSupport',
      'employment.totalMonthlyIncome',
    ],
  });

  const calculated =
    num(creditCards) + num(carLoans) + num(studentLoans) + num(otherDebts) + num(alimony);

  useEffect(() => {
    if (calculated > 0) {
      setValue('liabilities.totalMonthlyDebt', calculated, { shouldValidate: false, shouldDirty: false });
    }
  }, [calculated, setValue]);

  const dti =
    num(totalIncome) > 0 && calculated > 0
      ? ((calculated / num(totalIncome)) * 100).toFixed(1)
      : null;
  const dtiNum = dti != null ? parseFloat(dti) : null;
  const dtiSeverity =
    dtiNum == null ? 'info' : dtiNum <= 36 ? 'success' : dtiNum <= 43 ? 'warning' : 'error';

  return (
    <Box>
      <SectionHeader title="Liabilities" icon={<CreditCardIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Monthly debt obligations and recurring payments
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.creditCardDebt" label="Credit Card Payments" size="small" helperText="Total minimum monthly payments" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.carLoans" label="Auto Loans" size="small" helperText="Monthly car loan payments" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.studentLoans" label="Student Loans" size="small" helperText="Monthly student loan payments" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.otherDebts" label="Other Monthly Debts" size="small" helperText="Personal loans, medical debt, etc." />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.alimonyChildSupport" label="Alimony / Child Support" size="small" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="liabilities.totalMonthlyDebt" label="Total Monthly Debt" size="small" helperText="Sum of all monthly obligations" />
        </Grid>
      </Grid>

      {dti && (
        <Alert severity={dtiSeverity} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Debt-to-Income Ratio
          </Typography>
          <Typography variant="h4" component="p">
            {dti}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dtiNum <= 36
              ? 'Excellent — well within most lender requirements'
              : dtiNum <= 43
                ? 'Acceptable — meets standard qualification threshold'
                : 'High — may limit loan options or require additional documentation'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default LiabilitiesStep;
